import HealingIcon from "@mui/icons-material/Healing";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useNavigate, useParams } from "react-router";
import { EmptyState, LoadingBox } from "@/components/ui";
import { theme } from "@/theme/theme";
import { CommentAdd } from "../components/comments.tsx";
import { EditableVariableList } from "../components/editable-variable-list.tsx";
import { HarViewer } from "../components/har-viewer.tsx";
import { LiveView } from "../components/live-view.tsx";
import { LogsViewer } from "../components/logs-viewer.tsx";
import { RunStatusChip } from "../components/run-status-chip.tsx";
import { StorageStateViewer } from "../components/storage-state-viewer.tsx";
import { TestRunDownloadsTab } from "../components/test-run-downloads-tab.tsx";
import { TestRunStepsList } from "../components/test-run-steps-list.tsx";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import {
  type RunTestByIdParams,
  type Test,
  type TestRun,
  type TestRunLogs,
  TestRunStatusList,
  type TestRunStep,
} from "../types.ts";
import { formatDurationFromTimes } from "../utils";
import { humanizeText } from "../utils/text.ts";
import TestRunsPage from "./runs.tsx";

interface RunDetailsTabs {
  tab?:
    | "live"
    | "recording"
    | "network"
    | "logs"
    | "storage_state"
    | "code"
    | "variables"
    | "downloads"
    | "triage"
    | "results";
}

function mergeArtifactMap(
  previous: Record<string, string>,
  next?: Record<string, string> | null,
): Record<string, string> {
  if (!next) return previous;
  let hasChanges = false;
  const merged = { ...previous };

  for (const [contextName, path] of Object.entries(next)) {
    if (!merged[contextName] && path) {
      merged[contextName] = path;
      hasChanges = true;
    }
  }

  return hasChanges ? merged : previous;
}

export default function RunDetailsPage() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const userSelectedTabRef = useRef(false);

  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();

  const [finished, setFinished] = useState(false);

  const { params, setParam } = useTypedQueryParams<RunDetailsTabs>();

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      userSelectedTabRef.current = true;
      setParam("tab", newValue as RunDetailsTabs["tab"]);
    },
    [setParam],
  );

  const setTabParam = useCallback(
    (tabValue: RunDetailsTabs["tab"]) => {
      setParam("tab", tabValue);
    },
    [setParam],
  );

  const { runId } = useParams();

  const { data, isLoading, refetch } = useApiQuery<TestRun>(`/runs/${runId}`, {
    enabled: !!runId,
  });

  // Default to "live" only if run is running/pending, otherwise default to "recording"
  const getDefaultTab = useCallback(() => {
    if (data && !["running", "pending"].includes(data.run_status)) {
      return "recording";
    }
    return "live";
  }, [data]);

  // Get tab from URL, but validate it's available
  const isRunning = data && ["running", "pending"].includes(data.run_status);
  const urlTab = params.tab;

  // If "live" tab is requested but run is not running, fall back to default
  const tab = urlTab === "live" && !isRunning ? "recording" : (urlTab ?? getDefaultTab());

  const { data: logs, isLoading: isLogsLoading } = useApiQuery<TestRunLogs[]>(
    `/runs/${runId}/logs`,
    {
      enabled: !!runId,
      refetchInterval: data?.run_status === "running" ? 1_000 : undefined,
    },
  );

  const [stableRecordingPath, setStableRecordingPath] = useState<string | null>(null);
  const [stableHarPath, setStableHarPath] = useState<string | null>(null);
  const [stableContextRecordings, setStableContextRecordings] = useState<Record<string, string>>(
    {},
  );
  const [stableContextHars, setStableContextHars] = useState<Record<string, string>>({});
  const [stableContextLiveViews, setStableContextLiveViews] = useState<Record<string, string>>({});
  const [selectedContext, setSelectedContext] = useState<string | null>(null);
  const [storageStatePath, setStorageStatePath] = useState<string | null>(null);

  useEffect(() => {
    if (!runId) {
      return;
    }

    setStableRecordingPath(null);
    setStableHarPath(null);
    setStableContextRecordings({});
    setStableContextHars({});
    setStableContextLiveViews({});
    setStorageStatePath(null);
    setSelectedContext(null);
  }, [runId]);

  useEffect(() => {
    if (data?.recording_path && !stableRecordingPath) {
      setStableRecordingPath(data.recording_path);
    }
  }, [data?.recording_path, stableRecordingPath]);

  useEffect(() => {
    if (!data) return;
    if (["running", "pending"].includes(data.run_status)) return;
    if (data.har_path && !stableHarPath) {
      setStableHarPath(data.har_path);
    }
  }, [data, stableHarPath]);

  useEffect(() => {
    setStableContextRecordings((prev) => mergeArtifactMap(prev, data?.context_recordings));
  }, [data?.context_recordings]);

  useEffect(() => {
    setStableContextHars((prev) => mergeArtifactMap(prev, data?.context_hars));
  }, [data?.context_hars]);

  useEffect(() => {
    setStableContextLiveViews((prev) => mergeArtifactMap(prev, data?.context_live_views));
  }, [data?.context_live_views]);

  useEffect(() => {
    if (data?.storage_state_path && !storageStatePath) {
      setStorageStatePath(data.storage_state_path);
    }
  }, [data?.storage_state_path, storageStatePath]);

  const { data: test } = useApiQuery<Test>(`/tests/${data?.test_id}`, {
    enabled: !!data?.test_id,
    // No need to refetch test details every second
    refetchInterval: false,
  });

  const { data: steps, isLoading: stepsLoading } = useApiQuery<TestRunStep[]>(
    `/runs/${runId}/steps`,
    {
      enabled: !!runId,
      refetchInterval: data?.run_status === "running" ? 1_000 : 5_000,
    },
  );

  const lastUrl = useMemo(() => {
    if (!steps || steps.length === 0) return;
    const lastStep = steps[steps.length - 1];
    return lastStep?.url;
  }, [steps]);

  const contextOptions = useMemo(() => {
    const contexts = new Set<string>();
    const add = (artifacts?: Record<string, string> | null) => {
      if (!artifacts) return;
      for (const contextName of Object.keys(artifacts)) {
        contexts.add(contextName);
      }
    };

    add(stableContextLiveViews);
    add(data?.context_live_views);
    add(stableContextRecordings);
    add(data?.context_recordings);
    add(stableContextHars);
    add(data?.context_hars);

    return Array.from(contexts);
  }, [
    stableContextLiveViews,
    data?.context_live_views,
    stableContextRecordings,
    data?.context_recordings,
    stableContextHars,
    data?.context_hars,
  ]);

  const hasPerContextArtifacts = useMemo(() => {
    return (
      Object.keys(stableContextRecordings).length > 0 ||
      Object.keys(stableContextHars).length > 0 ||
      Object.keys(stableContextLiveViews).length > 0 ||
      Object.keys(data?.context_recordings || {}).length > 0 ||
      Object.keys(data?.context_hars || {}).length > 0 ||
      Object.keys(data?.context_live_views || {}).length > 0
    );
  }, [
    stableContextRecordings,
    stableContextHars,
    stableContextLiveViews,
    data?.context_recordings,
    data?.context_hars,
    data?.context_live_views,
  ]);

  useEffect(() => {
    if (contextOptions.length === 0) {
      setSelectedContext(null);
      return;
    }

    if (!selectedContext || !contextOptions.includes(selectedContext)) {
      setSelectedContext(contextOptions[0]);
    }
  }, [contextOptions, selectedContext]);

  const selectedRecordingPath = useMemo(() => {
    if (hasPerContextArtifacts && selectedContext) {
      return (
        stableContextRecordings[selectedContext] ??
        data?.context_recordings?.[selectedContext] ??
        null
      );
    }
    return stableRecordingPath ?? data?.recording_path ?? null;
  }, [
    hasPerContextArtifacts,
    selectedContext,
    stableContextRecordings,
    data?.context_recordings,
    stableRecordingPath,
    data?.recording_path,
  ]);

  const selectedHarPath = useMemo(() => {
    if (hasPerContextArtifacts && selectedContext) {
      return stableContextHars[selectedContext] ?? data?.context_hars?.[selectedContext] ?? null;
    }
    return stableHarPath ?? data?.har_path ?? null;
  }, [
    hasPerContextArtifacts,
    selectedContext,
    stableContextHars,
    data?.context_hars,
    stableHarPath,
    data?.har_path,
  ]);

  const selectedLiveViewUrl = useMemo(() => {
    if (hasPerContextArtifacts && selectedContext) {
      return (
        stableContextLiveViews[selectedContext] ??
        data?.context_live_views?.[selectedContext] ??
        undefined
      );
    }
    return data?.live_view_url;
  }, [
    hasPerContextArtifacts,
    selectedContext,
    stableContextLiveViews,
    data?.context_live_views,
    data?.live_view_url,
  ]);

  const { mutate: cancel, isPending: isCancelling } = useApiMutation<void>(
    "POST",
    `/runs/${runId}/cancel`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test Run cancelled", {
          variant: "warning",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });

        refetch();
      },
    },
  );

  const { mutate: update, isPending: isUpdating } = useApiMutation<TestRun, TestRun>(
    "PUT",
    `/runs/${runId}`,
    {
      onSuccess: () => {
        enqueueSnackbar("Test Run updated", {
          variant: "success",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });

        refetch();
      },
    },
  );

  useEffect(() => {
    setFinished(false);
    userSelectedTabRef.current = false;
    // Set default tab if there's no existing tab in URL
    // Use replace: true to avoid creating new history entries
    if (!params.tab) {
      setParam("tab", getDefaultTab(), { replace: true });
    }
  }, [setParam, params.tab, getDefaultTab]);

  useEffect(() => {
    if (!data) return;
    if (finished) return;

    // If run finishes and user is on live tab, switch to recording
    // Use replace: true since this is an automatic transition, not user-initiated
    if (!["running", "pending"].includes(data.run_status)) {
      if (tab === "live") {
        setParam("tab", "recording", { replace: true });
      }
      setFinished(true);
    }
  }, [data?.run_status, tab, finished, setParam, data]);

  const { mutate: rerun, isPending: isMutating } = useApiMutation<
    Pick<RunTestByIdParams, "allow_fix">,
    RunTestByIdParams
  >("POST", `/tests/${data?.test_id}/run?parent_run_id=${data?.id}`, {
    onSuccess: (resp: RunTestByIdParams) => {
      enqueueSnackbar(
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="body1">Test re-ran started!</Typography>
          <IconButton
            aria-label="View run"
            onClick={() => {
              nav(`/runs/${resp.run_id}`);
            }}
            size="small"
            color="inherit"
          >
            <OpenInNewIcon htmlColor={theme.palette.common.white} />
          </IconButton>
        </Stack>,
        {
          variant: "success",
          autoHideDuration: 2000,
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        },
      );

      setTabParam("triage");
      refetch();
    },
  });

  const duration = useMemo(() => {
    return formatDurationFromTimes(data?.start_time, data?.end_time, {
      showRunning: true,
    });
  }, [data?.start_time, data?.end_time]);

  const seekTo = useCallback((seconds: number) => {
    const v = videoRef.current;
    if (!v) return;

    // Pause the video if it's playing
    if (!v.paused) {
      v.pause();
    }

    if (v.readyState < 1) {
      // Wait until metadata is loaded so duration/currentTime are valid
      v.addEventListener(
        "loadedmetadata",
        () => {
          v.currentTime = seconds;
        },
        { once: true },
      );
    } else {
      v.currentTime = seconds;
    }
  }, []);

  if (isLoading) {
    return <LoadingBox />;
  }

  if (!data) {
    return <Typography>Run not found</Typography>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="column" spacing={1} alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="center">
            <RunStatusChip status={data.final_run_status || data.run_status} />
            {data.allow_fix && (
              <Tooltip title="This run used auto-fixing">
                <HealingIcon color="inherit" fontSize="inherit" sx={{ alignSelf: "center" }} />
              </Tooltip>
            )}
            <Tooltip title={test?.name || "No test name"}>
              <Typography variant="h5" noWrap maxWidth="400px">
                Run for "{test?.name || "..."}"
              </Typography>
            </Tooltip>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            divider={<Typography variant="body2">·</Typography>}
          >
            <Tooltip title="Start time">
              <Typography variant="body2">
                {new Date(data.start_time ?? data.created_at).toLocaleString()}
              </Typography>
            </Tooltip>
            {data.end_time ? (
              <Tooltip title="End time">
                <Typography variant="body2">{new Date(data.end_time).toLocaleString()}</Typography>
              </Tooltip>
            ) : (
              <Box className="loader" />
            )}
            {duration ? (
              <Typography variant="body2">{duration}</Typography>
            ) : (
              <Box className="loader" />
            )}
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          {["failed", "failed_pending", "skipped", "error", "cancelled"].includes(
            data.run_status,
          ) && (
            <Tooltip title="Re-run with auto-fix enabled">
              <IconButton
                color="warning"
                onClick={() => {
                  rerun({
                    allow_fix: true,
                  });
                }}
                loading={isLoading || isMutating || data?.run_status === "running"}
                disabled={isLoading || isMutating || data?.run_status === "running"}
                sx={{ margin: 2 }}
              >
                <HealingIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Re-run test. This will not auto-fix any issues by default, but will associate the run with this one.">
            <IconButton
              color="primary"
              onClick={() => {
                rerun({});
              }}
              loading={isLoading || isMutating || data?.run_status === "running"}
              disabled={isLoading || isMutating || data?.run_status === "running"}
              sx={{ margin: 2 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel Test Run">
            <Button
              variant="outlined"
              color="warning"
              disabled={
                !data.external_id ||
                !["running", "pending"].includes(data.run_status) ||
                isCancelling
              }
              loading={isCancelling}
              onClick={() => {
                cancel({});
              }}
            >
              Cancel
            </Button>
          </Tooltip>
          <Tooltip title="Go to Test">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                nav(`/tests-main/tests/${data.test_id}`);
              }}
            >
              Go to Test
            </Button>
          </Tooltip>
        </Stack>
      </Stack>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={65} minSize={40} maxSize={80}>
          <TabContext value={tab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={handleChange} aria-label="Run details tabs" variant="scrollable">
                {["running", "pending"].includes(data?.run_status || "") && (
                  <Tab label="Live" value="live" />
                )}
                <Tab label="Recording" value="recording" />
                <Tab label="Network" value="network" />
                <Tab label="Logs" value="logs" />
                <Tab label="State" value="storage_state" />
                <Tab label="Used variables" value="variables" />
                <Tab label="Downloads" value="downloads" />
                <Tab label="Triage" value="triage" />
                <Tab label="Results" value="results" />
              </TabList>
              {contextOptions.length > 1 && (
                <Tabs
                  value={selectedContext ?? false}
                  onChange={(_, value) => {
                    setSelectedContext(value);
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="Run context tabs"
                  sx={{
                    px: 1,
                    pt: 0.5,
                    "& .MuiTabs-indicator": {
                      display: "none",
                    },
                    "& .MuiTab-root": {
                      minHeight: 34,
                      textTransform: "none",
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                      border: "1px solid",
                      borderColor: "divider",
                      borderBottom: "none",
                      bgcolor: "action.hover",
                      mr: 0.5,
                    },
                    "& .MuiTab-root.Mui-selected": {
                      bgcolor: "background.paper",
                      fontWeight: 600,
                    },
                  }}
                >
                  {contextOptions.map((contextName) => (
                    <Tab key={contextName} value={contextName} label={contextName} />
                  ))}
                </Tabs>
              )}
            </Box>
            <TabPanel value="live">
              <LiveView
                pageUrl={lastUrl}
                liveViewUrl={
                  data.run_status === "running" && !(data.test_suite_run_id && !data.allow_fix)
                    ? selectedLiveViewUrl
                    : undefined
                }
                noLiveViewMessage={
                  !data.allow_fix && data.test_suite_run_id
                    ? "Live view is disabled for non-autofix tests that are part of a test suite run to improve performance. The recording will be available once the test completes."
                    : selectedLiveViewUrl
                      ? `Go to the recording tab to view ${
                          selectedContext ? `"${selectedContext}"` : "the"
                        } video.`
                      : "No live view available yet..."
                }
                height="500px"
              />
            </TabPanel>
            <TabPanel value="recording">
              {selectedRecordingPath ? (
                <Box mt={1}>
                  <video
                    key={selectedRecordingPath}
                    ref={videoRef}
                    controls
                    autoPlay
                    muted
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.grey[400]}`,
                    }}
                  >
                    <source
                      key={selectedRecordingPath}
                      src={selectedRecordingPath}
                      type={`video/${selectedRecordingPath.split("?")[0].split(".").pop()}`}
                    />
                    Your browser does not support the video tag.
                  </video>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {selectedContext
                    ? `No recording available yet for "${selectedContext}" context...`
                    : "No recording available yet..."}
                </Alert>
              )}
            </TabPanel>
            <TabPanel value="network">
              {selectedHarPath ? (
                <Box
                  sx={{
                    height: "calc(100vh - 200px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <HarViewer
                      key={selectedHarPath}
                      harPath={selectedHarPath}
                      testId={data?.test_id}
                      testRunId={runId}
                      appConfigId={test?.app_config_id}
                      showExpandButton={false}
                    />
                  </Box>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {selectedContext
                    ? `No HAR available yet for "${selectedContext}" context...`
                    : "No network data available yet..."}
                </Alert>
              )}
            </TabPanel>
            <TabPanel value="logs">
              {isLogsLoading && <LoadingBox sx={{ mt: 2 }} />}
              {logs && logs.length > 0 ? (
                <LogsViewer logs={logs} showExpandButton={false} />
              ) : (
                <EmptyState
                  variant="alert"
                  message="No logs available for this run."
                  sx={{ mt: 1 }}
                />
              )}
            </TabPanel>
            <TabPanel value="storage_state">
              {storageStatePath ? (
                <StorageStateViewer
                  storageStatePath={storageStatePath}
                  clipboard={data?.clipboard}
                />
              ) : (
                <EmptyState
                  variant="alert"
                  message="No storage state data available yet..."
                  sx={{ mt: 1 }}
                />
              )}
            </TabPanel>
            <TabPanel value="variables">
              <EditableVariableList variables={data.used_variables || []} hideTitle />
            </TabPanel>
            <TabPanel value="downloads">
              <TestRunDownloadsTab runId={runId ?? ""} />
            </TabPanel>
            <TabPanel value="triage">
              <Stack direction="column" spacing={1} alignItems="stretch" justifyContent="center">
                <Typography variant="caption" sx={{ pb: 1 }} color="text.secondary">
                  Update the final run status, which will update the parent run status to this
                  value. This can be useful when triaging a test run failure, when a subsequent
                  re-run pass, or just a false negative.
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Autocomplete
                    fullWidth
                    id="run-status"
                    size="small"
                    disabled={isUpdating}
                    loading={isUpdating}
                    options={TestRunStatusList}
                    getOptionLabel={(option) => {
                      return humanizeText(option);
                    }}
                    value={data.run_status}
                    onChange={(_, value) => {
                      update({
                        ...data,
                        run_status: value ?? data.run_status,
                      });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} multiline fullWidth label="Run Status" />
                    )}
                  />
                  <Autocomplete
                    fullWidth
                    id="final-run-status"
                    size="small"
                    disabled={isUpdating}
                    loading={isUpdating}
                    options={TestRunStatusList}
                    getOptionLabel={(option) => {
                      return humanizeText(option);
                    }}
                    value={data.final_run_status}
                    onChange={(_, value) => {
                      update({
                        ...data,
                        final_run_status: value ?? undefined,
                      });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} multiline fullWidth label="Final Run Status" />
                    )}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Leave a final comment for this run.
                </Typography>
                <CommentAdd
                  entityType="test_run"
                  entityId={data.id}
                  commentId={data.final_comment_id}
                  snackbar={false}
                  label="Final comment"
                  onSuccess={(comment) => {
                    update({
                      ...data,
                      final_comment_id: comment.id,
                    });
                  }}
                />
                <Divider sx={{ pt: 2 }} />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  pb={1}
                  pt={1}
                >
                  <Typography variant="subtitle1">Re-runs</Typography>
                  {data.rerun_id && (
                    <Tooltip title="View parent run">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          nav(`/runs/${data.rerun_id}`);
                        }}
                      >
                        View parent
                      </Button>
                    </Tooltip>
                  )}
                </Stack>
                <TestRunsPage rerunId={data.id} noHeader />
              </Stack>
            </TabPanel>
            <TabPanel value="results">
              <Stack direction="column" spacing={1} alignItems="stretch">
                <Card sx={{ p: 2 }}>
                  {data.result ? (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {data.result}
                    </Typography>
                  ) : (
                    <EmptyState variant="alert" message="No results available yet..." />
                  )}
                </Card>
                <Card sx={{ p: 2 }}>
                  {data.error_message ? (
                    <Stack direction="column" spacing={2} divider={<Divider />}>
                      <Typography variant="subtitle2">Error</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }} color="error">
                        {data.error_message}
                      </Typography>
                    </Stack>
                  ) : (
                    <Alert severity="success">No errors!</Alert>
                  )}
                </Card>
              </Stack>
            </TabPanel>
          </TabContext>
        </Panel>
        <PanelResizeHandle>
          <Divider orientation="vertical">
            <MultipleStopIcon color="disabled" fontSize="small" />
          </Divider>
        </PanelResizeHandle>
        <Panel defaultSize={35} minSize={20} maxSize={60}>
          <TestRunStepsList
            steps={steps}
            isLoading={stepsLoading}
            recordingPath={selectedRecordingPath || undefined}
            onStepClick={(step) => {
              if (!selectedRecordingPath) {
                return;
              }
              const runCreated = new Date(data.created_at).getTime();
              const stepCreated = new Date(step.created_at).getTime();
              const videoTime = (stepCreated - runCreated) / 1_000;
              seekTo(videoTime);
            }}
          />
        </Panel>
      </PanelGroup>
    </Box>
  );
}
